/**
 * Password policy — client mirror of the server rule.
 *
 * The authority is the backend: `audio-sanctum-backend-main/src/common/
 * validation/password.ts`, enforced on every DTO that sets a password. This
 * copy exists only so the UI can show per-rule feedback as the user types
 * instead of round-tripping per keystroke. Keep the two in step — if a rule
 * changes there, change it here, or the form will accept something the API
 * then rejects.
 *
 * Rule (user decision 2026-07-19): at least 8 characters, and ALL FOUR of
 * lowercase, uppercase, digit, and symbol. There is deliberately NO
 * common-password blocklist — it shipped and was removed the same day, so
 * "Password1!" and similar are accepted.
 */

export const PASSWORD_MIN_LENGTH = 8;

const RE_LOWER = /[a-z]/;
const RE_UPPER = /[A-Z]/;
const RE_DIGIT = /[0-9]/;
const RE_SYMBOL = /[^A-Za-z0-9]/;

export type PasswordRuleKey = "length" | "lower" | "upper" | "digit" | "symbol";

export type PasswordCheck = Record<PasswordRuleKey, boolean>;

export function checkPassword(password: string): PasswordCheck {
  const pw = typeof password === "string" ? password : "";
  return {
    length: pw.length >= PASSWORD_MIN_LENGTH,
    lower: RE_LOWER.test(pw),
    upper: RE_UPPER.test(pw),
    digit: RE_DIGIT.test(pw),
    symbol: RE_SYMBOL.test(pw),
  };
}

export function isStrongPassword(password: string): boolean {
  return Object.values(checkPassword(password)).every(Boolean);
}

/** The rules shown as a checklist, in order. */
export const PASSWORD_RULES: { key: PasswordRuleKey; label: string }[] = [
  { key: "length", label: `At least ${PASSWORD_MIN_LENGTH} characters` },
  { key: "lower", label: "One lowercase letter" },
  { key: "upper", label: "One uppercase letter" },
  { key: "digit", label: "One number" },
  { key: "symbol", label: "One special character" },
];

/** Single message for the form-level error, matching the server's wording. */
export function firstPasswordProblem(password: string): string | null {
  const c = checkPassword(password);
  if (!c.length) return `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`;
  if (!c.lower) return "Password must include a lowercase letter";
  if (!c.upper) return "Password must include an uppercase letter";
  if (!c.digit) return "Password must include a number";
  if (!c.symbol) return "Password must include a special character";
  return null;
}
